const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Número do dia no calendário LOCAL. Contar dias pelas partes locais (e não subtraindo
 * milissegundos) evita errar "ontem" perto da meia-noite ou em dias de 23/25 horas.
 */
function localDayNumber(date: Date): number {
  return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY);
}

let weekdayFormat: Intl.DateTimeFormat | undefined;
const weekdayName = (date: Date): string => {
  weekdayFormat ??= new Intl.DateTimeFormat('pt-BR', { weekday: 'short' });
  return weekdayFormat.format(date);
};

let fullFormat: Intl.DateTimeFormat | undefined;

/** Data e hora por extenso no fuso de quem olha ("sexta-feira, 16 de outubro de 2026 às 14:30"). */
export function formatFullDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  fullFormat ??= new Intl.DateTimeFormat('pt-BR', { dateStyle: 'full', timeStyle: 'short' });
  return fullFormat.format(date);
}

/**
 * Quando a nota foi escrita, em linguagem de gente: "agora mesmo", "há 12 min", "hoje às 14:30",
 * "ontem às 14:30", "ter. às 14:30" (até 6 dias) e, depois, "dd/MM/yyyy às 14:30". A data completa
 * fica no `title` do <time>; aqui só o resumo. Um instante no futuro (relógio do aparelho atrasado
 * em relação ao servidor) conta como "agora mesmo" em vez de mostrar "daqui a N min".
 */
export function formatNoteTime(iso: string, now: Date): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const elapsed = now.getTime() - date.getTime();
  if (elapsed < MS_PER_MINUTE) return 'agora mesmo';
  if (elapsed < 60 * MS_PER_MINUTE) return `há ${Math.floor(elapsed / MS_PER_MINUTE)} min`;

  const clock = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  const daysAgo = localDayNumber(now) - localDayNumber(date);
  if (daysAgo <= 0) return `hoje às ${clock}`;
  if (daysAgo === 1) return `ontem às ${clock}`;
  if (daysAgo <= 6) return `${weekdayName(date)} às ${clock}`;
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()} às ${clock}`;
}
